import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Activity, Users, Flame, Compass, Filter, FileText, ShieldCheck, Lightbulb, Radio } from 'lucide-react';
import { Card } from './os2ui';
import { authFetch } from '../../lib/auth';

/* Business Pulse — "What is happening in my business and what should I do now?"
   100% first-party data (web_events). No GA4 clone: intelligence + action. */

const fmt = (n) => (Number(n) || 0).toLocaleString();
const agoMin = (iso, isEs) => {
  if (!iso) return '';
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 1) return isEs ? 'ahora mismo' : 'just now';
  if (m < 60) return isEs ? `hace ${m} min` : `${m} min ago`;
  const h = Math.round(m / 60);
  return isEs ? `hace ${h} h` : `${h}h ago`;
};

const INTENT_META = {
  high: { es: 'Alta intención', en: 'High intent', cls: 'bg-red-50 text-red-600 border-red-200' },
  evaluating: { es: 'Evaluando', en: 'Evaluating', cls: 'bg-orange-50 text-[#d8651a] border-orange-200' },
  learning: { es: 'Aprendiendo', en: 'Learning', cls: 'bg-sky-50 text-sky-600 border-sky-200' },
  browsing: { es: 'Explorando', en: 'Browsing', cls: 'bg-gray-50 text-gray-500 border-gray-200' },
};

function PulseKpi({ label, value, sub, testid, accent }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-gray-400">{label}</p>
      <p className={`mt-1 text-[26px] font-black tabular-nums leading-none ${accent || 'text-gray-900'}`}>{value}</p>
      {sub && <p className="mt-1 text-[10.5px] text-gray-400">{sub}</p>}
    </Card>
  );
}

function Sparkline({ trend }) {
  if (!trend?.length) return null;
  const w = 220; const h = 44;
  const max = Math.max(...trend.map((t) => t.views), 1);
  const pts = trend.map((t, i) => `${(i / (trend.length - 1)) * w},${h - (t.views / max) * (h - 6)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-11" role="img" aria-label="visitors trend">
      <polyline points={pts} fill="none" stroke="#F77F11" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

function HBar({ label, value, max, right, onClick, testid }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag onClick={onClick} data-testid={testid}
      className={`flex items-center gap-2 py-1 w-full text-left ${onClick ? 'group cursor-pointer rounded-lg -mx-1 px-1 hover:bg-orange-50/60 transition-colors' : ''}`}>
      <span className={`w-32 sm:w-40 shrink-0 truncate text-[11.5px] text-gray-600 ${onClick ? 'group-hover:text-[#d8651a] group-hover:underline underline-offset-2 decoration-dotted' : ''}`} title={label}>{label}</span>
      <div className="flex-1 h-3 rounded-full bg-gray-100 overflow-hidden">
        <div className="h-full rounded-full bg-[#F77F11] transition-all duration-500" style={{ width: `${Math.max(3, (value / (max || 1)) * 100)}%` }} />
      </div>
      <span className="w-[76px] shrink-0 text-right text-[11.5px] font-bold tabular-nums text-gray-800">{right ?? fmt(value)}</span>
    </Tag>
  );
}

function DrillDrawer({ drill, onClose, isEs }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    setD(null); setErr(null);
    authFetch(`/growth/pulse/drill?dim=${drill.dim}&key=${encodeURIComponent(drill.key)}${drill.days ? `&days=${drill.days}` : ''}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status))).then(setD).catch((e) => setErr(String(e)));
  }, [drill]);
  const maxS = Math.max(...((d?.top_sources || []).map((x) => x.sessions)), 1);
  const maxL = Math.max(...((d?.top_landing || []).map((x) => x.sessions)), 1);
  return (
    <div className="fixed inset-0 z-[80]" data-testid="pulse-drill">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px]" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl overflow-y-auto p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#F77F11]">
              {drill.dim === 'source' ? (isEs ? 'Fuente de adquisición' : 'Acquisition source') : drill.dim === 'landing' ? (isEs ? 'Página de entrada' : 'Landing page') : (isEs ? 'Nivel de intención' : 'Intent level')}
            </p>
            <h3 className="mt-1 text-[17px] font-bold text-gray-900 break-all">{drill.label || drill.key}</h3>
          </div>
          <button onClick={onClose} data-testid="pulse-drill-close" className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-colors">✕</button>
        </div>
        {err && <p className="mt-4 text-[12px] text-red-500">{isEs ? 'No se pudo cargar la evidencia.' : 'Could not load the evidence.'}</p>}
        {!d && !err && <p className="mt-4 text-[12px] text-gray-400 animate-pulse">{isEs ? 'Reuniendo la evidencia…' : 'Gathering the evidence…'}</p>}
        {d && (
          <div className="mt-4 space-y-4">
            <p className="text-[11px] text-gray-400" data-testid="pulse-drill-window">
              {isEs ? 'Ventana' : 'Window'}: <b className="text-gray-600">{d.window.start.slice(0, 10)} → {d.window.end.slice(0, 10)}</b> · {d.window.days}d
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3">
                <p className="text-[9.5px] uppercase tracking-wide font-bold text-gray-400">{isEs ? 'Sesiones' : 'Sessions'}</p>
                <p className="text-[22px] font-black tabular-nums text-gray-900" data-testid="pulse-drill-sessions">{fmt(d.sessions)}</p>
              </div>
              <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3">
                <p className="text-[9.5px] uppercase tracking-wide font-bold text-gray-400">{isEs ? 'Conversiones' : 'Conversions'}</p>
                <p className="text-[22px] font-black tabular-nums text-gray-900">{fmt(d.conversions)}</p>
              </div>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-gray-400">{isEs ? 'Intención de esas sesiones' : 'Intent of those sessions'}</p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {Object.entries(INTENT_META).map(([k, m]) => (
                  <span key={k} className={`rounded-full border px-2 py-0.5 text-[10.5px] font-bold ${m.cls}`}>{isEs ? m.es : m.en} · {d.intent[k] || 0}</span>
                ))}
              </div>
            </div>
            {drill.dim !== 'source' && d.top_sources?.length > 0 && (
              <div>
                <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-gray-400">{isEs ? 'De dónde vinieron' : 'Where they came from'}</p>
                <div className="mt-1">{d.top_sources.map((s) => <HBar key={s.source} label={s.source} value={s.sessions} max={maxS} />)}</div>
              </div>
            )}
            {drill.dim !== 'landing' && d.top_landing?.length > 0 && (
              <div>
                <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-gray-400">{isEs ? 'Por dónde entraron' : 'Where they landed'}</p>
                <div className="mt-1">{d.top_landing.map((l) => <HBar key={l.path} label={l.path} value={l.sessions} max={maxL} />)}</div>
              </div>
            )}
            {d.journeys?.length > 0 && (
              <div>
                <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-gray-400">{isEs ? 'Recorridos reales (últimos)' : 'Real journeys (latest)'}</p>
                {d.journeys.map((j, i) => (
                  <div key={i} className="py-1.5 border-b border-gray-50 last:border-0">
                    <p className="flex items-center gap-1.5 text-[10.5px] text-gray-400">
                      <span className={`rounded-full border px-1.5 py-0.5 text-[9px] font-bold uppercase ${INTENT_META[j.intent]?.cls}`}>{isEs ? INTENT_META[j.intent]?.es : INTENT_META[j.intent]?.en}</span>
                      {j.ref} · {agoMin(j.last_at, isEs)}
                    </p>
                    <p className="mt-0.5 text-[11.5px] text-gray-700 break-all">{(j.pages || []).map((p) => p.path).join(' → ') || '—'}</p>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[10px] text-gray-300">{isEs ? d.method_es : d.method_en}</p>
          </div>
        )}
      </aside>
    </div>
  );
}

const heatTone = (s) => (s >= 65 ? 'text-emerald-600 bg-emerald-50' : s >= 30 ? 'text-[#d8651a] bg-orange-50' : 'text-gray-500 bg-gray-100');
const SEO_DOT = { pass: '🟢', warn: '🟠', fail: '🔴', pending: '⚪' };
const SEO_TONE = { green: 'bg-emerald-50 text-emerald-700 border-emerald-200', amber: 'bg-amber-50 text-amber-700 border-amber-200', red: 'bg-red-50 text-red-700 border-red-200' };

const SRC_STATE = {
  ok: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500', es: 'OK', en: 'OK' },
  tracking_active: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500', es: 'tracking activo', en: 'tracking active' },
  connected: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500', es: 'conectado · recibiendo datos', en: 'connected · receiving data' },
  temporarily_unavailable: { cls: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500', es: 'temporalmente no disponible', en: 'temporarily unavailable' },
  auth_expired: { cls: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500', es: 'autorización caducada', en: 'authorization expired' },
  permission_required: { cls: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500', es: 'permiso requerido', en: 'permission required' },
  setup_required: { cls: 'bg-gray-50 text-gray-500 border-gray-200', dot: 'bg-gray-400', es: 'configurar', en: 'setup required' },
};

function RealtimeCard({ rt, isEs }) {
  const label = (
    <p className="mb-2 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-bold text-gray-400">
      <Radio className="w-3.5 h-3.5 text-[#F77F11]" /> {isEs ? 'GA4 · Tiempo real' : 'GA4 · Realtime'}
      <span className="rounded-full bg-red-50 text-red-600 border border-red-200 px-1.5 py-0.5 text-[9px] font-bold normal-case tracking-normal">LIVE</span>
    </p>
  );
  if (!rt || rt.status === 'setup_required') {
    return (
      <Card className="p-4" data-testid="pulse-realtime">
        {label}
        <p className="text-[12.5px] text-gray-500">{isEs ? 'Conecta GA4 (Reporting) desde el Growth Center para ver usuarios reales en tiempo real.' : 'Connect GA4 (Reporting) from the Growth Center to see real users in realtime.'}</p>
      </Card>
    );
  }
  if (rt.status !== 'ok') {
    const m = SRC_STATE[rt.status] || SRC_STATE.temporarily_unavailable;
    return (
      <Card className="p-4" data-testid="pulse-realtime">
        {label}
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold ${m.cls}`} data-testid="pulse-realtime-state">
          <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} /> {isEs ? m.es : m.en}
        </span>
        <p className="mt-2 text-[11.5px] text-gray-500">
          {rt.status === 'auth_expired'
            ? (isEs ? 'Reconecta Google desde el Growth Center para reanudar los datos.' : 'Reconnect Google from the Growth Center to resume data.')
            : (isEs ? 'Google no responde ahora mismo — reintento automático. No se muestran ceros falsos.' : 'Google is not responding right now — auto-retry. No fake zeros shown.')}
        </p>
      </Card>
    );
  }
  const mx = Math.max(...rt.by_minute.map((m) => m.users), 1);
  return (
    <Card className="p-4" data-testid="pulse-realtime">
      {label}
      <p className="text-[30px] font-black tabular-nums leading-none text-gray-900" data-testid="pulse-realtime-users">{(rt.active_users || 0).toLocaleString()}</p>
      <p className="text-[10.5px] text-gray-400 mt-1">{isEs ? `usuarios activos · últimos ${rt.window_min} min · actualizado` : `active users · last ${rt.window_min} min · refreshed`} {agoMin(rt.fetched_at, isEs)}</p>
      <div className="mt-2 flex items-end gap-[2px] h-8" data-testid="pulse-realtime-minutes">
        {rt.by_minute.slice().reverse().map((m, i) => (
          <div key={i} title={`${m.minutes_ago} min: ${m.users}`} className="flex-1 rounded-sm bg-[#F77F11]/80"
            style={{ height: `${Math.max(8, (m.users / mx) * 100)}%`, opacity: m.users ? 1 : 0.15 }} />
        ))}
      </div>
      {rt.top_pages?.length > 0 && (
        <div className="mt-2 space-y-0.5">
          {rt.top_pages.slice(0, 4).map((p) => (
            <p key={p.title} className="flex items-center justify-between gap-2 text-[11px] text-gray-600">
              <span className="truncate">{p.title}</span><b className="tabular-nums shrink-0">{p.users}</b>
            </p>
          ))}
        </div>
      )}
      <p className="mt-2 text-[9.5px] text-gray-300">{isEs ? rt.note_es : rt.note_en}</p>
    </Card>
  );
}

function SectionLabel({ icon: Icon, children }) {
  return <p className="mb-2 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-bold text-gray-400"><Icon className="w-3.5 h-3.5 text-[#F77F11]" /> {children}</p>;
}

const getRangeDays = () => {
  const d = localStorage.getItem('kx_os2_days') || '';
  return d === 'yd' ? '1' : d;
};

export default function BusinessPulse({ isEs }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [rangeDays, setRangeDays] = useState(getRangeDays());
  const [drill, setDrill] = useState(null);
  useEffect(() => {
    const on = () => setRangeDays(getRangeDays());
    window.addEventListener('kx-os2-range', on);
    return () => window.removeEventListener('kx-os2-range', on);
  }, []);
  const load = useCallback(() => {
    authFetch(`/growth/pulse${rangeDays ? `?days=${rangeDays}` : ''}`).then((r) => (r.ok ? r.json() : Promise.reject(r.status))).then(setD).catch((e) => setErr(String(e)));
  }, [rangeDays]);
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  if (err) return null;
  if (!d) return <Card className="p-6 text-center text-[12.5px] text-gray-400" data-testid="pulse-loading">{isEs ? 'Leyendo el pulso del negocio…' : 'Reading the business pulse…'}</Card>;

  const v = d.visitors;
  const W = d.window?.days ?? 7;
  const wl = `${W}d`;
  const oppTone = d.opportunity.score >= 65 ? '#10b981' : d.opportunity.score >= 40 ? '#F77F11' : '#ef4444';
  const circ = 2 * Math.PI * 26;
  const maxAcq = Math.max(...(d.acquisition.map((a) => a.visitors)), 1);
  const totAcq = d.acquisition.reduce((s, a) => s + a.visitors, 0) || 1;
  const maxLand = Math.max(...(d.landing_pages.map((l) => l.sessions)), 1);
  const totLand = d.landing_pages.reduce((s, l) => s + l.sessions, 0) || 1;
  const totIntent = Object.values(d.intent).reduce((s, n) => s + n, 0) || 1;
  const maxFunnel = Math.max(...(d.funnel.steps.map((s) => s.count)), 1);

  return (
    <div className="space-y-5" data-testid="business-pulse">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-[12px] uppercase tracking-[0.15em] font-bold text-gray-500">
          <Activity className="w-4 h-4 text-[#F77F11]" /> Business Pulse
          <span className="normal-case tracking-normal font-medium text-gray-400">— {isEs ? '¿Qué está pasando y qué hago ahora?' : 'What is happening and what should I do now?'}</span>
          <span className="rounded-full border border-orange-200 bg-orange-50 text-[#d8651a] px-2 py-0.5 text-[10px] font-bold normal-case tracking-normal" data-testid="pulse-window-badge">
            {d.window ? `${d.window.start.slice(0, 10)} → ${d.window.end.slice(0, 10)}` : wl}
          </span>
        </p>
        <button onClick={load} data-testid="pulse-refresh" className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 hover:border-[#F77F11] px-3 py-1.5 text-[11.5px] font-semibold text-gray-600 transition-colors">
          <RefreshCw className="w-3 h-3" /> {isEs ? 'Actualizar' : 'Refresh'}
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <PulseKpi testid="pulse-kpi-today" label={isEs ? 'Vistas hoy' : 'Views today'} value={fmt(v.today_views)}
          sub={v.today_visitors != null ? `${fmt(v.today_visitors)} ${isEs ? 'visitantes únicos' : 'unique visitors'}` : (isEs ? `ayer: ${fmt(v.yesterday_views)}` : `yesterday: ${fmt(v.yesterday_views)}`)} />
        <PulseKpi testid="pulse-kpi-active" label={isEs ? 'Activos ahora' : 'Active now'} value={fmt(d.active_now.count)}
          sub={isEs ? `últimos ${d.active_now.window_min} min` : `last ${d.active_now.window_min} min`}
          accent={d.active_now.count > 0 ? 'text-emerald-600' : undefined} />
        <PulseKpi testid="pulse-kpi-7d" label={isEs ? `Vistas ${wl}` : `Views ${wl}`} value={fmt(v.views_7d)}
          sub={v.trend_pct != null ? `${v.trend_pct >= 0 ? '+' : ''}${v.trend_pct}% ${isEs ? 'vs periodo previo' : 'vs prev period'}` : (isEs ? 'sin periodo previo' : 'no prior period')}
          accent={v.trend_pct != null && v.trend_pct < 0 ? 'text-red-500' : undefined} />
        <PulseKpi testid="pulse-kpi-intent" label={isEs ? `Alta intención ${wl}` : `High intent ${wl}`} value={fmt(d.intent.high)}
          sub={isEs ? 'sesiones en precios/demo/contacto' : 'sessions on pricing/demo/contact'} accent="text-[#d8651a]" />
        <Card className="p-4 flex items-center gap-3" data-testid="pulse-kpi-opportunity">
          <svg width="60" height="60" viewBox="0 0 64 64" className="shrink-0">
            <circle cx="32" cy="32" r="26" fill="none" stroke="#e5e7eb" strokeWidth="6" />
            <circle cx="32" cy="32" r="26" fill="none" stroke={oppTone} strokeWidth="6" strokeLinecap="round"
              strokeDasharray={`${(d.opportunity.score / 100) * circ} ${circ}`} transform="rotate(-90 32 32)" />
            <text x="32" y="37" textAnchor="middle" fontSize="16" fontWeight="800" fill="#111827">{d.opportunity.score}</text>
          </svg>
          <div>
            <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-gray-400">Opportunity Score</p>
            <p className="text-[10.5px] text-gray-400 leading-snug mt-0.5">{isEs ? 'SEO + tráfico + intención + conversión' : 'SEO + traffic + intent + conversion'}</p>
          </div>
        </Card>
      </div>

      {/* Realtime + Trend + live journeys */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <RealtimeCard rt={d.realtime} isEs={isEs} />
        <Card className="p-4" data-testid="pulse-trend">
          <SectionLabel icon={Activity}>{isEs ? `Tendencia ${v.trend?.length || 14} días` : `${v.trend?.length || 14}-day trend`}</SectionLabel>
          <Sparkline trend={v.trend} />
          <p className="mt-1 text-[10.5px] text-gray-400">
            {v.has_visitor_ids
              ? (isEs ? 'Vistas de página · visitantes únicos activándose (IDs anónimos recién estrenados)' : 'Page views · unique visitors ramping up (anonymous IDs just launched)')
              : (isEs ? 'Vistas de página first-party (los visitantes únicos empiezan a contarse desde hoy)' : 'First-party page views (unique visitors start counting from today)')}
          </p>
        </Card>
        <Card className="p-4" data-testid="pulse-journeys">
          <SectionLabel icon={Radio}>{isEs ? 'Recorridos en vivo' : 'Live visitor journeys'}</SectionLabel>
          {d.active_now.journeys.length ? d.active_now.journeys.map((j, i) => (
            <div key={i} className="py-1.5 border-b border-gray-50 last:border-0" data-testid={`pulse-journey-${i}`}>
              <p className="flex items-center gap-1.5 text-[11px]">
                <span className={`rounded-full border px-1.5 py-0.5 text-[9.5px] font-bold uppercase ${INTENT_META[j.intent]?.cls}`}>{isEs ? INTENT_META[j.intent]?.es : INTENT_META[j.intent]?.en}</span>
                <span className="text-gray-400">{j.ref} · {agoMin(j.last_at, isEs)}</span>
              </p>
              <p className="mt-0.5 text-[11.5px] text-gray-700 truncate">{(j.pages || []).map((p) => p.path).join(' → ') || '—'}</p>
            </div>
          )) : (
            <p className="text-[12px] text-gray-400 py-3">{isEs ? 'Nadie navegando en este momento — el radar sigue escuchando.' : 'No one browsing right now — the radar keeps listening.'}</p>
          )}
        </Card>
      </div>

      {/* Acquisition · Landing · Intent */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="p-4" data-testid="pulse-acquisition">
          <SectionLabel icon={Compass}>{isEs ? `Adquisición ${wl}` : `Acquisition ${wl}`} <span className="normal-case tracking-normal font-semibold text-gray-300">· {fmt(totAcq)} {isEs ? 'total' : 'total'}</span></SectionLabel>
          {d.acquisition.length ? d.acquisition.map((a) => (
            <HBar key={a.source} label={a.source} value={a.visitors} max={maxAcq}
              right={`${fmt(a.visitors)} · ${Math.round((a.visitors / totAcq) * 100)}%`}
              testid={`pulse-acq-${a.source.replace(/[^a-z0-9]+/gi, '-')}`}
              onClick={() => setDrill({ dim: 'source', key: a.source, label: a.source, days: rangeDays })} />
          )) : <p className="text-[12px] text-gray-400">{isEs ? 'Sin visitas en este periodo' : 'No visits in this period'}</p>}
        </Card>
        <Card className="p-4" data-testid="pulse-landing">
          <SectionLabel icon={FileText}>{isEs ? `Páginas de entrada ${wl}` : `Landing pages ${wl}`} <span className="normal-case tracking-normal font-semibold text-gray-300">· {fmt(totLand)} {isEs ? 'sesiones' : 'sessions'}</span></SectionLabel>
          {d.landing_pages.length ? d.landing_pages.map((l) => (
            <HBar key={l.path} label={l.path} value={l.sessions} max={maxLand}
              right={`${fmt(l.sessions)} · ${Math.round((l.sessions / totLand) * 100)}%`}
              testid={`pulse-land-${l.path.replace(/[^a-z0-9]+/gi, '-')}`}
              onClick={() => setDrill({ dim: 'landing', key: l.path, label: l.path, days: rangeDays })} />
          )) : <p className="text-[12px] text-gray-400">—</p>}
        </Card>
        <Card className="p-4" data-testid="pulse-intent">
          <SectionLabel icon={Filter}>{isEs ? `Intención de las sesiones ${wl}` : `Session intent ${wl}`}</SectionLabel>
          <div className="space-y-2 mt-1">
            {Object.entries(INTENT_META).map(([k, m]) => (
              <button key={k} onClick={() => setDrill({ dim: 'intent', key: k, label: isEs ? m.es : m.en, days: rangeDays })}
                data-testid={`pulse-intent-${k}`}
                className="flex items-center gap-2 w-full text-left group cursor-pointer rounded-lg -mx-1 px-1 py-0.5 hover:bg-orange-50/60 transition-colors">
                <span className={`rounded-full border px-2 py-0.5 text-[10.5px] font-bold ${m.cls}`}>{isEs ? m.es : m.en}</span>
                <span className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                  <span className="block h-full rounded-full bg-[#F77F11]/70 transition-all duration-500" style={{ width: `${Math.max(2, (d.intent[k] / totIntent) * 100)}%` }} />
                </span>
                <b className="tabular-nums text-[13px] text-gray-800 shrink-0">{fmt(d.intent[k])} <span className="text-[10px] font-semibold text-gray-400">{Math.round((d.intent[k] / totIntent) * 100)}%</span></b>
              </button>
            ))}
          </div>
        </Card>
      </div>

      {/* Funnel */}
      <Card className="p-4" data-testid="pulse-funnel">
        <SectionLabel icon={Filter}>{isEs ? `Embudo de negocio · ${d.funnel.window_days} días` : `Business funnel · ${d.funnel.window_days} days`}</SectionLabel>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {d.funnel.steps.map((s, i) => (
            <div key={s.key} className="rounded-xl border border-gray-100 bg-gray-50/60 p-3 text-center" data-testid={`pulse-funnel-${s.key}`}>
              <p className="text-[10px] uppercase tracking-wide font-bold text-gray-400">{isEs ? s.label_es : s.label_en}</p>
              <p className="mt-1 text-[20px] font-black tabular-nums text-gray-900">{fmt(s.count)}</p>
              <div className="mt-1.5 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                <div className="h-full rounded-full bg-[#F77F11]" style={{ width: `${Math.max(2, (s.count / maxFunnel) * 100)}%` }} />
              </div>
              {i > 0 && <p className="mt-1 text-[10px] text-gray-400 tabular-nums">{s.rate}%</p>}
            </div>
          ))}
        </div>
      </Card>

      {/* Heat + Content Radar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className="p-4" data-testid="pulse-heat">
          <SectionLabel icon={Flame}>{isEs ? `Heat Score por página ${wl}` : `Page Heat Score ${wl}`}</SectionLabel>
          {d.heat.length ? d.heat.map((h) => (
            <div key={h.path} className="flex items-center gap-3 py-1.5 border-b border-gray-50 last:border-0" data-testid={`pulse-heat-${h.path.replace(/[^a-z0-9]+/gi, '-')}`}>
              <span className={`w-11 shrink-0 text-center rounded-lg py-1 text-[13px] font-black tabular-nums ${heatTone(h.score)}`}>{h.score}</span>
              <div className="min-w-0 flex-1">
                <p className="text-[12.5px] font-semibold text-gray-800 truncate">{h.path}</p>
                <p className="text-[10.5px] text-gray-400">{fmt(h.views)} {isEs ? 'vistas' : 'views'} · {h.signals.engaged_60s} engaged · {h.signals.scroll_90} scroll 90% · {h.signals.conversions} conv.</p>
              </div>
            </div>
          )) : <p className="text-[12px] text-gray-400">{isEs ? 'Acumulando señales de comportamiento…' : 'Accumulating behavior signals…'}</p>}
          <p className="mt-2 text-[10px] text-gray-300">{isEs ? 'Heat = tiempo >60s + scroll 90% + conversiones, sobre vistas reales.' : 'Heat = 60s dwell + 90% scroll + conversions, over real views.'}</p>
        </Card>
        <Card className="p-4" data-testid="pulse-content-radar">
          <SectionLabel icon={FileText}>Content Radar</SectionLabel>
          {d.content_radar.length ? d.content_radar.map((c) => (
            <div key={c.slug} className="flex items-center gap-3 py-1.5 border-b border-gray-50 last:border-0">
              <div className="min-w-0 flex-1">
                <p className="text-[12.5px] font-semibold text-gray-800 truncate">{c.slug}</p>
                <p className="text-[10.5px] text-gray-400">{c.avg_seconds}s {isEs ? 'lectura media' : 'avg read'} · {c.avg_depth}% {isEs ? 'profundidad' : 'depth'}</p>
              </div>
              <span className="shrink-0 text-[12px] font-bold tabular-nums text-gray-800">{fmt(c.reads)}
                <span className={`ml-1 text-[10px] ${c.reads >= c.reads_prev ? 'text-emerald-600' : 'text-red-500'}`}>({c.reads >= c.reads_prev ? '+' : ''}{c.reads - c.reads_prev})</span>
              </span>
            </div>
          )) : <p className="text-[12px] text-gray-400">{isEs ? 'Sin lecturas de magazine esta semana.' : 'No magazine reads this week.'}</p>}
        </Card>
      </div>

      {/* SEO Health + Recommendations */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Card className="p-4" data-testid="pulse-seo-health">
          <SectionLabel icon={ShieldCheck}>SEO Health</SectionLabel>
          <span className={`inline-flex rounded-full border px-2.5 py-1 text-[10.5px] font-bold uppercase ${SEO_TONE[d.seo_health.overall]}`} data-testid="pulse-seo-overall">
            {d.seo_health.overall === 'green' ? (isEs ? '🟢 Saludable' : '🟢 Healthy') : d.seo_health.overall === 'amber' ? (isEs ? '🟠 Atención' : '🟠 Attention') : (isEs ? '🔴 Crítico' : '🔴 Critical')} · {d.seo_health.passed}/{d.seo_health.total}
          </span>
          <div className="mt-2.5 space-y-1">
            {d.seo_health.checks.map((c) => (
              <p key={c.id} className="text-[11.5px] text-gray-600">{SEO_DOT[c.status]} {isEs ? c.label_es : c.label_en}{c.detail && <span className="text-gray-300"> · {c.detail}</span>}</p>
            ))}
          </div>
        </Card>
        <Card className="p-4 lg:col-span-2" data-testid="pulse-recommendations">
          <SectionLabel icon={Lightbulb}>{isEs ? 'AI Radar — qué hacer ahora' : 'AI Radar — what to do now'}</SectionLabel>
          <div className="space-y-2.5">
            {d.opportunity.recommendations.map((r, i) => (
              <div key={i} className="rounded-xl border border-gray-100 bg-gray-50/60 border-l-4 p-3"
                style={{ borderLeftColor: r.priority === 'high' ? '#ef4444' : r.priority === 'medium' ? '#F77F11' : '#10b981' }}
                data-testid={`pulse-rec-${i}`}>
                <p className={`text-[9.5px] uppercase tracking-wide font-bold ${r.priority === 'high' ? 'text-red-500' : r.priority === 'medium' ? 'text-[#d8651a]' : 'text-emerald-600'}`}>
                  {r.priority === 'high' ? (isEs ? 'Prioridad alta' : 'High priority') : r.priority === 'medium' ? (isEs ? 'Prioridad media' : 'Medium priority') : (isEs ? 'Todo en orden' : 'All good')}
                  {r.source && <span className="ml-1.5 rounded bg-white border border-gray-200 text-gray-500 px-1.5 py-0.5 normal-case tracking-normal">{r.source}</span>}
                </p>
                <p className="mt-0.5 text-[13px] font-semibold text-gray-900 leading-snug">{isEs ? r.title_es : r.title_en}</p>
                <p className="mt-0.5 text-[11.5px] text-gray-600 leading-relaxed">{isEs ? r.detail_es : r.detail_en}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Sources footer — truthful connection states */}
      <div className="flex flex-wrap items-center gap-1.5" data-testid="pulse-sources">
        {[['GA4 Tag', d.sources.ga4_tag], ['GA4 Reporting', d.sources.ga4_reporting], ['Search Console', d.sources.search_console], ['Clarity', d.sources.clarity], ['First-party', d.sources.first_party]].map(([label, s]) => {
          if (!s) return null;
          const m = SRC_STATE[s.status] || SRC_STATE.setup_required;
          return (
            <span key={label} title={(isEs ? s.note_es : s.note_en) || (s.last_success_at ? `${isEs ? 'último dato' : 'last data'}: ${agoMin(s.last_success_at, isEs)}` : '')}
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-bold ${m.cls}`}
              data-testid={`pulse-source-${label.toLowerCase().replace(/\s/g, '-')}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
              {label} · {isEs ? m.es : m.en}
              {s.cached && s.status === 'temporarily_unavailable' && <span className="opacity-70">({isEs ? 'caché' : 'cached'})</span>}
            </span>
          );
        })}
        <span className="text-[10px] text-gray-300 ml-1">{isEs ? 'Datos first-party + Google APIs · tráfico interno excluido automáticamente' : 'First-party data + Google APIs · internal traffic auto-excluded'}</span>
      </div>
      {drill && <DrillDrawer drill={drill} onClose={() => setDrill(null)} isEs={isEs} />}
    </div>
  );
}
